/**
 * 1440 — Central analytics layer (Mixpanel + PostHog mirror)
 *
 * WHY THIS FILE EXISTS
 * Before this, Mixpanel.init() was called in ~16 separate components, each with
 * its own config (some track_pageview: true, some not), and the project token was
 * hardcoded in every one of them. That produced duplicate automatic pageviews,
 * made token rotation a 16-file edit, and left whole surfaces (phone auth/OTP,
 * the coach chat, the Stripe checkout, the static homepage) with no tracking at
 * all.
 *
 * RULES
 * 1. Mixpanel is initialised EXACTLY ONCE, here, via initAnalytics().
 * 2. No component should ever import "mixpanel-browser" directly again.
 *    Import { track, identify, EVENTS } from "utils/analytics" instead.
 * 3. Automatic pageview capture is OFF. Pageviews are emitted by <RouteTracker/>
 *    on real route changes, so SPA navigation is counted once and only once.
 * 4. Every event carries the shared context (path, device, session, UTM), so no
 *    caller has to remember to attach it.
 */

import Mixpanel from "mixpanel-browser";
import posthog from "posthog-js";

/** Project token. Env var wins so the token can be rotated without a code edit. */
const MIXPANEL_TOKEN =
  process.env.REACT_APP_MIXPANEL_TOKEN || "d724d0d0045430023cc58dd17086256b";

const SESSION_ID_KEY = "1440_analytics_session_id";
const UTM_KEY = "1440_utm";
const LANDING_TIME_KEY = "1440_analytics_landing_time";

let initialized = false;
let identifiedUserId = null;
let eventCount = 0;

/**
 * QA marker: mirror analytics liveness onto the <html> element.
 * Browser automation and isolated-world consoles cannot read page globals like
 * window.mixpanel, so without this there is no way to prove from the outside
 * that tracking is actually running in production. Cheap, and it makes the
 * "did this deploy really instrument the app?" question answerable.
 */
function mark(name, value) {
  try {
    document.documentElement.setAttribute(`data-1440-${name}`, String(value));
  } catch (e) {
    /* SSR / no DOM */
  }
}

/* ------------------------------------------------------------------ *
 * Canonical event taxonomy.
 * Naming: "Object Action" in Title Case. Add new events HERE so the
 * whole codebase shares one vocabulary and typos cannot invent events.
 * ------------------------------------------------------------------ */
export const EVENTS = {
  // Lifecycle / navigation
  APP_LOADED: "App Loaded",
  PAGE_VIEWED: "Page Viewed",

  // Marketing site + CTAs
  CTA_CLICKED: "CTA Clicked",
  OUTBOUND_LINK_CLICKED: "Outbound Link Clicked",
  WHATSAPP_CLICKED: "WhatsApp Click",
  SMS_CLICKED: "SMS Click",
  MESSENGER_CLICKED: "Messenger Click",

  // Phone auth funnel
  SIGNUP_STARTED: "Signup Started",
  OTP_REQUESTED: "OTP Requested",
  OTP_REQUEST_FAILED: "OTP Request Failed",
  OTP_RESENT: "OTP Resent",
  OTP_SUBMITTED: "OTP Submitted",
  OTP_VERIFIED: "OTP Verified",
  OTP_VERIFICATION_FAILED: "OTP Verification Failed",
  SIGNUP_COMPLETED: "Signup Completed",
  LOGGED_OUT: "Logged Out",

  // Onboarding
  ONBOARDING_STARTED: "Onboarding Started",
  ONBOARDING_STEP_COMPLETED: "Onboarding Step Completed",
  ONBOARDING_STEP_BACK: "Onboarding Step Back",
  ONBOARDING_COMPLETED: "Onboarding Completed",

  // Coach chat (the core product loop)
  CHAT_OPENED: "Chat Opened",
  CHAT_VIEW_CHANGED: "Chat View Changed",
  MESSAGE_SENT: "Message Sent",
  COACH_RESPONSE_RECEIVED: "Coach Response Received",
  COACH_RESPONSE_FAILED: "Coach Response Failed",
  FIRST_MESSAGE_SENT: "First Message Sent",
  FIRST_RESPONSE_RECEIVED: "First Response Received",

  // Integrations
  HEALTH_CONNECT_STARTED: "Health Connect Started",
  CALENDAR_CONNECT_STARTED: "Calendar Connect Started",
  CONNECTED_PAGE_VIEWED: "Connected Page Viewed",

  // Money
  PAYWALL_VIEWED: "Paywall Viewed",
  PLAN_SELECTED: "Plan Selected",
  CHECKOUT_STARTED: "Checkout Started",
  CHECKOUT_FAILED: "Checkout Failed",

  // Assessments
  ASSESSMENT_STARTED: "Assessment Started",
  ASSESSMENT_COMPLETED: "Assessment Completed",

  // Media / misc
  VIDEO_STARTED: "Video Started",
  VIDEO_SKIPPED: "Video Skipped",
  CARD_DOWNLOADED: "Card Downloaded",
  THEME_TOGGLED: "Theme Toggled",
  ERROR_SHOWN: "Error Shown",
};

/* ------------------------------------------------------------------ *
 * Context helpers
 * ------------------------------------------------------------------ */

function safeStorage(get) {
  try {
    return get();
  } catch (e) {
    return null;
  }
}

function getSessionId() {
  return (
    safeStorage(() => {
      let id = sessionStorage.getItem(SESSION_ID_KEY);
      if (!id) {
        id = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
        sessionStorage.setItem(SESSION_ID_KEY, id);
      }
      return id;
    }) || "sess_unavailable"
  );
}

function getLandingTime() {
  const stored = safeStorage(() => {
    let t = sessionStorage.getItem(LANDING_TIME_KEY);
    if (!t) {
      t = String(Date.now());
      sessionStorage.setItem(LANDING_TIME_KEY, t);
    }
    return t;
  });
  return stored ? parseInt(stored, 10) : Date.now();
}

function getDeviceType() {
  const ua = (navigator.userAgent || "").toLowerCase();
  if (/ipad|tablet/.test(ua)) return "tablet";
  if (/mobile|android|iphone|ipod|blackberry|iemobile|opera mini/.test(ua))
    return "mobile";
  return "desktop";
}

/** Capture UTM params on first load and persist them for the whole session. */
function captureUtm() {
  try {
    const params = new URLSearchParams(window.location.search);
    const fields = [
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_content",
      "utm_term",
      "gclid",
      "fbclid",
    ];
    const found = {};
    fields.forEach((f) => {
      const v = params.get(f);
      if (v) found[f] = v;
    });
    if (Object.keys(found).length) {
      sessionStorage.setItem(UTM_KEY, JSON.stringify(found));
      return found;
    }
    const stored = sessionStorage.getItem(UTM_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (e) {
    return {};
  }
}

function getUtm() {
  const stored = safeStorage(() => sessionStorage.getItem(UTM_KEY));
  try {
    return stored ? JSON.parse(stored) : {};
  } catch (e) {
    return {};
  }
}

/** Properties attached to EVERY event so callers never have to think about it. */
function baseProperties() {
  return {
    session_id: getSessionId(),
    page_path: window.location.pathname,
    page_url: window.location.href,
    page_title: document.title,
    referrer: document.referrer || null,
    device_type: getDeviceType(),
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    time_since_landing_ms: Date.now() - getLandingTime(),
    app_surface: "web",
    ...getUtm(),
  };
}

/* ------------------------------------------------------------------ *
 * Public API
 * ------------------------------------------------------------------ */

/**
 * Initialise Mixpanel once. Safe to call repeatedly (later calls no-op).
 * NOTE track_pageview is deliberately false, see rule 3 at the top.
 */
export function initAnalytics() {
  if (initialized) return;
  initialized = true;

  try {
    Mixpanel.init(MIXPANEL_TOKEN, {
      track_pageview: false,
      persistence: "localStorage",
      ignore_dnt: false,
      batch_requests: true,
    });
    mark("analytics", "ready");
    mark("token", MIXPANEL_TOKEN.slice(0, 6));

    const utm = captureUtm();
    getLandingTime();

    // Register super properties so they land on every event, including
    // Mixpanel's own internal ones.
    Mixpanel.register({
      app_surface: "web",
      session_id: getSessionId(),
      device_type: getDeviceType(),
      ...utm,
    });

    // First-touch attribution: set once, never overwritten on later visits.
    if (Object.keys(utm).length && Mixpanel.people) {
      Mixpanel.people.set_once({
        first_touch_utm_source: utm.utm_source || null,
        first_touch_utm_campaign: utm.utm_campaign || null,
        first_touch_landing_page: window.location.pathname,
      });
    }
  } catch (e) {
    mark("analytics", "init-failed");
    mark("error", String(e && e.message ? e.message : e).slice(0, 80));
    console.warn("[analytics] Mixpanel init failed:", e);
  }
}

/** Track an event to Mixpanel and mirror it to PostHog. Never throws. */
export function track(event, properties = {}) {
  if (!event) return;
  const payload = { ...baseProperties(), ...properties };

  try {
    if (!initialized) initAnalytics();
    Mixpanel.track(event, payload);
    eventCount += 1;
    mark("events", eventCount);
    mark("last-event", event);
  } catch (e) {
    mark("error", String(e && e.message ? e.message : e).slice(0, 80));
    console.warn(`[analytics] mixpanel track failed for "${event}":`, e);
  }

  // Mirror to PostHog so the two tools never disagree about what happened.
  try {
    if (posthog && typeof posthog.capture === "function") {
      posthog.capture(event, payload);
    }
  } catch (e) {
    /* posthog is optional, never let it break the app */
  }
}

/** Tie all subsequent events to a user id. Idempotent per id. */
export function identify(userId, traits = {}) {
  if (!userId || identifiedUserId === userId) return;
  identifiedUserId = userId;
  try {
    if (!initialized) initAnalytics();
    Mixpanel.identify(userId);
    if (Mixpanel.people) {
      Mixpanel.people.set({
        last_seen_at: new Date().toISOString(),
        last_device_type: getDeviceType(),
        ...traits,
      });
      Mixpanel.people.set_once({ first_seen_at: new Date().toISOString() });
    }
  } catch (e) {
    console.warn("[analytics] identify failed:", e);
  }
  try {
    if (posthog && typeof posthog.identify === "function") {
      posthog.identify(userId, traits);
    }
  } catch (e) {
    /* optional */
  }
}

/** Set person properties without emitting an event. */
export function setUserProperties(traits = {}) {
  try {
    if (!initialized) initAnalytics();
    if (Mixpanel.people) Mixpanel.people.set(traits);
  } catch (e) {
    /* best effort */
  }
}

/** Increment a numeric person property (message counts etc). */
export function incrementUserProperty(name, by = 1) {
  try {
    if (!initialized) initAnalytics();
    if (Mixpanel.people) Mixpanel.people.increment(name, by);
  } catch (e) {
    /* best effort */
  }
}

/** Clear identity on logout so the next user is not merged into this one. */
export function resetAnalytics() {
  identifiedUserId = null;
  try {
    Mixpanel.reset();
  } catch (e) {
    /* best effort */
  }
}

/**
 * Fire a "first time only" event, deduped in localStorage.
 * Used for First Message Sent / First Response Received so the activation
 * funnel is not polluted by every later message.
 */
export function trackOnce(key, event, properties = {}) {
  const storageKey = `1440_once_${key}`;
  const already = safeStorage(() => localStorage.getItem(storageKey));
  if (already) return false;
  safeStorage(() => localStorage.setItem(storageKey, String(Date.now())));
  track(event, properties);
  return true;
}

/** Convenience wrapper for pageviews. */
export function trackPageView(extra = {}) {
  track(EVENTS.PAGE_VIEWED, extra);
}

/**
 * Named-page helper so legacy call sites that used to do
 * Mixpanel.track("Page View", { page: "about" }) keep the same intent
 * while emitting the canonical event.
 */
export function trackNamedPage(pageName, extra = {}) {
  track(EVENTS.PAGE_VIEWED, { page: pageName, ...extra });
}

const analytics = {
  EVENTS,
  initAnalytics,
  track,
  trackOnce,
  trackPageView,
  trackNamedPage,
  identify,
  setUserProperties,
  incrementUserProperty,
  resetAnalytics,
};

export default analytics;
